// SimPage – SIM list view: fetches SIM stats, enriches with usage, and delegates to SimList.

import React, { useEffect, useState } from 'react';
import { Sim } from '../../types/Sim';
import SimList from '../components/SimList/SimList';
import { rendererLogger } from '../logger';

// ---- Types ----

type SortConfig = {
  key: keyof Sim | 'last_came_online_at' | 'totalMB';
  direction: 'asc' | 'desc';
};

// ---- Component ----

export function SimPage() {
  // Data from main process: list of SIMs and loading/error state.
  const [sims, setSims] = useState<Sim[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // UI state: filter (activated only), sort column/dir, pagination, which row is expanded.
  const [activatedOnly, setActivatedOnly] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedSim, setExpandedSim] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<'sim' | 'data' | 'device' | null>(null);

  /** Fetch all SIM stats from main process; enrich each SIM with totalMB and sessionCount from usage. */
  const fetchData = () => {
    rendererLogger.info('[SimPage] Fetching SIM data...');
    setLoading(true);
    window.simAPI
      .getAllSimStats()
      .then((res) => {
        const raw = res?.success && Array.isArray(res?.data?.results) ? res.data.results : [];
        const enriched: Sim[] = raw.map((sim: Sim) => {
          const totalBytes = Array.isArray(sim.usage)
            ? sim.usage.reduce((sum, r) => sum + (r.bytes ?? 0), 0)
            : 0;
          return {
            ...sim,
            totalMB: parseFloat((totalBytes / 1024 / 1024).toFixed(2)),
            sessionCount: Array.isArray(sim.usage) ? sim.usage.length : 0,
          };
        });
        // Log and push to state so SimList can render.
        rendererLogger.info(`[SimPage] Processed ${enriched.length} SIMs`);
        setSims(enriched);
        setError(res?.error ?? null);
      })
      .catch((err) => {
        rendererLogger.error('[SimPage] Error fetching SIMs: ' + (err?.message ?? String(err)));
        setError(err?.message ?? 'Failed to load SIMs');
        setSims([]);
      })
      .finally(() => {
        rendererLogger.info('[SimPage] Finished fetching SIM data');
        setLoading(false);
      });
  };

  /** Load SIMs once on mount. */
  useEffect(() => {
    fetchData();
  }, []);

  /** Wire sort: same column toggles asc/desc; new column starts asc. */
  const handleSort = (key: SortConfig['key']) => {
    const direction =
      sortConfig?.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  /** All UI is in SimList; we just pass data and handlers. */
  return (
    <SimList
      sims={sims}
      loading={loading}
      error={error}
      activatedOnly={activatedOnly}
      sortConfig={sortConfig}
      currentPage={currentPage}
      expandedSim={expandedSim}
      openSection={openSection}
      onToggleActivatedOnly={setActivatedOnly}
      onSort={handleSort}
      onToggleExpand={(id) => {
        setExpandedSim((prev) => (prev === id ? null : id));
        setOpenSection(null);
      }}
      onPageChange={setCurrentPage}
      onRefresh={fetchData}
      onSectionToggle={setOpenSection}
    />
  );
}
